import * as React from 'react';
import {Eye} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {AirbnbFieldTrigger} from '../field-trigger';
import {cn} from '../../lib/cn';

type AirbnbInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  topLabel?: string;
  helperText?: string;
  helper?: React.ReactNode;
  error?: string;
  invalid?: boolean;
  loading?: boolean;
  optional?: boolean | string;
  tooltip?: React.ReactNode;
  wrapperClassName?: string;
  fieldClassName?: string;
  contentClassName?: string;
  inputClassName?: string;
  trailingAdornment?: React.ReactNode;
  renderErrorMessage?: boolean;
};

const getInputValue = (
  value: AirbnbInputProps['value'] | AirbnbInputProps['defaultValue'],
) => (value != null ? String(value) : '');

const AirbnbInput = React.forwardRef<HTMLInputElement, AirbnbInputProps>(
  (
    {
      label,
      topLabel,
      helperText,
      helper,
      error,
      invalid,
      loading,
      optional,
      tooltip,
      wrapperClassName,
      fieldClassName,
      contentClassName,
      inputClassName,
      trailingAdornment,
      renderErrorMessage = true,
      className,
      type,
      id,
      value,
      defaultValue,
      disabled,
      onChange,
      onFocus,
      onBlur,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const {t} = useTranslation();
    const generatedId = React.useId();
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const inputId = id ?? generatedId;
    const fieldId = `${inputId}-field`;
    const labelId = `${inputId}-label`;
    const errorId = `${inputId}-error`;
    const accessibleLabel = placeholder ?? label;
    const [isFocused, setIsFocused] = React.useState(false);
    const [isPasswordRevealed, setIsPasswordRevealed] = React.useState(false);
    const [currentValue, setCurrentValue] = React.useState(() =>
      value != null ? getInputValue(value) : getInputValue(defaultValue),
    );
    const resolvedValue = value != null ? getInputValue(value) : currentValue;
    const hasValue = resolvedValue.length > 0;
    const isPasswordInput = type === 'password';
    const inputType = isPasswordInput && isPasswordRevealed ? 'text' : type;
    const shouldShowPasswordReveal = isPasswordInput && hasValue && !loading;
    const shouldShowLabel = hasValue || isFocused;
    const hasInvalidState = Boolean(error) || Boolean(invalid);
    const triggerError = error ?? invalid;
    const hasLabelMeta = Boolean(optional) || Boolean(tooltip);
    const isBlocked = Boolean(disabled) || Boolean(loading);

    React.useLayoutEffect(() => {
      const nextValue =
        value != null ? getInputValue(value) : getInputValue(inputRef.current?.value);

      setCurrentValue(prevValue => (prevValue === nextValue ? prevValue : nextValue));
    }, [value]);

    React.useEffect(() => {
      if (!isPasswordInput) {
        setIsPasswordRevealed(false);
      }
    }, [isPasswordInput]);

    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;

        if (node && value == null) {
          setCurrentValue(prevValue =>
            prevValue === node.value ? prevValue : node.value,
          );
        }

        if (typeof ref === 'function') {
          ref(node);
          return;
        }

        if (ref) {
          ref.current = node;
        }
      },
      [ref, value],
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(event.target.value);
      onChange?.(event);
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(event);
    };

    const togglePasswordReveal = () => {
      setIsPasswordRevealed(isRevealed => !isRevealed);
    };

    const focusInput = () => {
      if (isBlocked) {
        return;
      }

      inputRef.current?.focus();
    };

    return (
      <div className={cn('w-full max-w-[var(--max-field-width)]', wrapperClassName)}>
        <AirbnbFieldTrigger
          as="div"
          id={fieldId}
          label={accessibleLabel ?? ''}
          topLabel={topLabel}
          labelId={labelId}
          helperTextId={!shouldShowLabel ? `${inputId}-helper` : undefined}
          errorId={error ? errorId : undefined}
          labelText={helperText ?? label}
          placeholder={placeholder ?? label}
          helper={helper}
          disabled={disabled}
          error={triggerError}
          loading={loading}
          optional={optional}
          tooltip={tooltip}
          onClick={focusInput}
          describedBy={error && renderErrorMessage ? errorId : undefined}
          className={cn(
            'px-4 focus-within:ring-2 focus-within:ring-[var(--chekin-airbnb-ink)] focus-within:ring-offset-2',
            disabled ? 'cursor-not-allowed' : 'cursor-text',
            fieldClassName,
          )}
          contentClassName={cn('h-[42px]', contentClassName)}
          trailingAdornment={shouldShowPasswordReveal ? undefined : trailingAdornment}
          forceFloatingLabel={shouldShowLabel}
          forceLabelText={hasLabelMeta}
          hideErrorMessage={!renderErrorMessage}
        >
          <input
            {...props}
            id={inputId}
            ref={setRefs}
            type={inputType}
            disabled={isBlocked}
            value={value}
            defaultValue={defaultValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder=""
            aria-invalid={hasInvalidState}
            aria-busy={loading}
            aria-describedby={error && renderErrorMessage ? errorId : undefined}
            aria-label={accessibleLabel && hasLabelMeta ? accessibleLabel : undefined}
            aria-labelledby={accessibleLabel && !hasLabelMeta ? labelId : undefined}
            className={cn(
              'absolute left-0 h-6 w-full border-0 bg-transparent p-0 text-[16px] leading-6 text-[var(--chekin-airbnb-ink)] outline-none placeholder:text-[var(--chekin-airbnb-gray-cool)]',
              '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&[type=number]]:[-moz-appearance:textfield]',
              'bottom-0',
              hasInvalidState
                ? 'text-[var(--status-danger)] placeholder:text-[var(--status-danger)]'
                : '',
              disabled ? 'cursor-not-allowed' : loading ? 'cursor-progress' : '',
              shouldShowPasswordReveal ? 'pr-8' : '',
              inputClassName,
              className,
            )}
          />
          {shouldShowPasswordReveal && (
            <button
              type="button"
              onClick={togglePasswordReveal}
              disabled={isBlocked}
              className="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 text-[var(--chekin-airbnb-gray-cool)] hover:text-[var(--chekin-airbnb-ink)] hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={isPasswordRevealed ? t('hide_password') : t('show_password')}
            >
              <Eye
                size={18}
                aria-hidden="true"
                className={cn(
                  isPasswordRevealed ? 'text-[var(--chekin-airbnb-pink)]' : '',
                )}
              />
            </button>
          )}
        </AirbnbFieldTrigger>
      </div>
    );
  },
);

AirbnbInput.displayName = 'AirbnbInput';

export {AirbnbInput};
export type {AirbnbInputProps};
